import React, { useEffect } from 'react';

import { FlagModule, MessageModule } from '@atlassian/connect-module-core';

import { FlagProvider, MessageProvider } from '@confluence/connect';
import { withFlags } from '@confluence/flags';
import { useSessionData } from '@confluence/session-data';
import { RequireLegacyWRM } from '@confluence/wrm';
import { keys } from '@confluence/storage-manager';
import { DataProviderModule } from '@confluence/app-referentiality/entry-points/DataProviderModule';

import { CcAnalyticsModule } from './CcAnalyticsModule';
import { MarketplaceModule } from './MarketplaceModule';
import { ConnectAnalyticsPublishSupport } from './ConnectAnalyticsPublishSupport';
import { isIframeInDocument } from './ConnectSupportUtils';

const ConnectSupportComponent = ({ flags }) => {
	const { activationId, cloudId } = useSessionData();

	// For Confluence App Analytics. This code works in conjunction with monolith's
	// confluence-plugins/confluence-bundled-plugins/atlassian-connect/jsapi-v5/src/main/resources/v5/velocity/iframe-body-script.vm.
	// It adds a listener to the initial HTML page that stores events if this ConnectSupport component has not mounted yet.
	// Here, we process the missed event data and disable the initial listener.
	useEffect(() => {
		window.__CONFLUENCE_CONNECT_SUPPORT_LOADED__ = true;
		// We're not using the CFE provided @confluence/storage-manager because
		// this package is not available in the monolith.
		const events =
			JSON.parse(window.localStorage.getItem(keys.CONNECT_APP_ANALYTICS) || `""`) || [];
		events.forEach((e) => {
			void ConnectAnalyticsPublishSupport.sendAppAnalyticsEvent(activationId, e['extension']);
		});
		window.localStorage.removeItem(keys.CONNECT_APP_ANALYTICS);
	}, [activationId]);

	return (
		<RequireLegacyWRM
			wrm={[
				// resource to provide the context value of AP.navigator.getLocation
				'wr!confluence.web.resources:querystring',
				'wr!confluence.web.resources:navigator-context',
				'wr!com.atlassian.plugins.atlassian-connect-plugin:confluence-atlassian-connect-resources-v5',
			]}
		>
			{() => {
				if (!window.connectHost) {
					return null;
				}
				const connectHost = window.connectHost;

				const flagProvider = new FlagProvider(flags);
				const messageProvider = new MessageProvider(flags);
				const adaptor = connectHost.getFrameworkAdaptor();
				/**
				 * Subscribe to ACJS internal events for iframe performance and iframe timeout events
				 * Then use the analytics client from confluence to track those events.
				 */

				try {
					connectHost.onIframePerformanceTelemetry((eventData) => {
						void ConnectAnalyticsPublishSupport.trackIframePerformanceTelemetry(
							eventData.extension,
							eventData.metrics,
						);
					});
					connectHost.onIframeTimeout(async (eventData) => {
						if (eventData.extension && isIframeInDocument(eventData.extension.id)) {
							void ConnectAnalyticsPublishSupport.trackIframeTimeout(eventData.extension);
						} else {
							void ConnectAnalyticsPublishSupport.trackInvalidIframeTimeout(eventData.extension);
						}
					});
					connectHost.onIframeEstablished(async (eventData) => {
						void ConnectAnalyticsPublishSupport.trackIframeLoaded(eventData.extension);

						void ConnectAnalyticsPublishSupport.sendAppAnalyticsEvent(
							activationId,
							eventData.extension,
						);
					});
					connectHost.onIframeVisible(async (eventData) => {
						void ConnectAnalyticsPublishSupport.trackIframeViewed(eventData.extension);
					});
					connectHost.onContentResolverEvent?.(async (eventData) => {
						void ConnectAnalyticsPublishSupport.trackContentResolverEvent(eventData);
					});
					connectHost?.onForwardAnalyticsEvent?.(async (eventType, eventData) => {
						void ConnectAnalyticsPublishSupport.handleForwardedAnalyticsEvent(eventType, eventData);
					});
				} catch (e) {}

				return (
					<span data-test-id="connect-support-component">
						<FlagModule flagProvider={flagProvider} adaptor={adaptor} />
						<MessageModule messageProvider={messageProvider} adaptor={adaptor} />
						<CcAnalyticsModule key="ccanalytics" cloudId={cloudId} connectHost={connectHost} />
						<MarketplaceModule key="marketplace" cloudId={cloudId} />
						<DataProviderModule connectHost={connectHost} />
					</span>
				);
			}}
		</RequireLegacyWRM>
	);
};

export const ConnectSupport = withFlags(ConnectSupportComponent);
